import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/pages/home/components/Navbar";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || "/";

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || "로그인에 실패했습니다");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-16">
        <div className="w-full max-w-[420px]">
          {/* Logo mark */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#ff6eb4] to-[#4fc3f7] mx-auto mb-4">
              <i className="ri-sparkling-2-fill text-white text-2xl" />
            </div>
            <h1 className="text-2xl font-black text-white tracking-tight">로그인</h1>
            <p className="text-white/40 text-sm mt-1.5">CreatorVerse에 오신 걸 환영해요!</p>
          </div>

          {/* Social Login */}
          <div className="flex flex-col gap-3 mb-6">
            <button className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-white/15 text-white/80 text-sm font-medium hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap">
              <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="Google" />
              Google로 계속하기
            </button>
            <button className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-white/15 text-white/80 text-sm font-medium hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-kakao-talk-fill text-[#3A1D1D] text-base leading-none" style={{ color: "#3A1D1D" }} />
              </div>
              카카오로 계속하기
            </button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/30 text-xs">또는 이메일로 로그인</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="text-white/50 text-xs font-semibold mb-2 block">이메일</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-white/30 text-sm" />
                </div>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="example@email.com"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-white/50 text-xs font-semibold">비밀번호</label>
                <button type="button" className="text-[#ff6eb4] text-xs hover:underline cursor-pointer whitespace-nowrap">
                  비밀번호 찾기
                </button>
              </div>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-white/30 text-sm" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="비밀번호 입력"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-11 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
                >
                  <i className={`text-white/30 text-sm ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-[#ff4757]/10 border border-[#ff4757]/30 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line text-[#ff4757] text-sm flex-shrink-0" />
                <p className="text-[#ff4757] text-xs">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-bold text-sm text-white cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #ff6eb4, #4fc3f7)" }}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  로그인 중...
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line" />
                  로그인
                </>
              )}
            </button>
          </form>

          {/* Hint */}
          <div className="mt-4 p-3 rounded-xl bg-white/[0.03] border border-white/8">
            <p className="text-white/30 text-xs text-center leading-relaxed">
              <i className="ri-information-line mr-1" />
              테스트: 아무 이메일(@포함) + 8자 이상 비밀번호로 로그인하세요
            </p>
          </div>

          <p className="text-center text-white/30 text-xs mt-5">
            아직 계정이 없으신가요?{" "}
            <Link to="/signup" className="text-[#ff6eb4] hover:underline font-medium cursor-pointer">
              회원가입
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
